import { useEffect, useState } from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useTargetPublishedChecklists } from "../../../lib/api";

/**
 * ImportTypeSelector Component
 *
 * Allows user to choose between importing as a new checklist or as a revision
 * If revision is selected, shows a list of published checklists to select from
 */
interface ImportTypeSelectorProps {
  className?: string;
}

export function ImportTypeSelector({ className }: ImportTypeSelectorProps) {
  const {
    targetFacilityId,
    targetUseCaseId,
    targetImportType,
    setTargetImportType,
    targetChecklistId,
    setTargetChecklistId,
    setTargetAuthorId
  } = useWorkflowStore();

  const [searchTerm, setSearchTerm] = useState("");

  // Fetch published checklists only when revision is selected
  const {
    data: publishedChecklists = [],
    isLoading,
    error
  } = useTargetPublishedChecklists(
    targetImportType === 'revision' ? targetFacilityId : null,
    targetImportType === 'revision' ? targetUseCaseId : null
  );

  // Filter checklists based on search term
  const filteredChecklists = publishedChecklists.filter((checklist: any) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      checklist.code?.toLowerCase().includes(searchLower) ||
      checklist.name?.toLowerCase().includes(searchLower)
    );
  });

  // Reset checklist selection when import type changes
  useEffect(() => {
    setTargetChecklistId(null);
    setTargetAuthorId(null);
    setSearchTerm("");
  }, [targetImportType, setTargetChecklistId, setTargetAuthorId]);

  // Reset when facility or use case changes
  useEffect(() => {
    setTargetImportType(null);
    setTargetChecklistId(null);
    setSearchTerm("");
  }, [targetFacilityId, targetUseCaseId, setTargetImportType, setTargetChecklistId]);

  const isDisabled = !targetFacilityId || !targetUseCaseId;

  const handleImportTypeChange = (value: string) => {
    setTargetImportType(value as 'new' | 'revision');
  };

  const handleChecklistSelect = (checklistId: string) => {
    // Toggle selection
    setTargetChecklistId(targetChecklistId === checklistId ? null : checklistId);
  };

  const getPlaceholderText = () => {
    if (!targetFacilityId || !targetUseCaseId) return "Select facility and use case first";
    return "Select import type";
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-4">
        <div className="space-y-2">
          <Label htmlFor="import-type">Import Type</Label>
          <Select
            value={targetImportType || ""}
            onValueChange={handleImportTypeChange}
            disabled={isDisabled}
          >
            <SelectTrigger id="import-type">
              <SelectValue placeholder={getPlaceholderText()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">New Checklist</SelectItem>
              <SelectItem value="revision">Revision of existing checklist</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Show checklist list only when revision is selected */}
        {targetImportType === 'revision' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Published Checklists</h3>
              <span className="text-sm text-muted-foreground">
                {targetChecklistId ? '1 selected' : '0 selected'}
              </span>
            </div>

            {/* Search bar */}
            <Input
              type="text"
              placeholder="Search by checklist code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />

            {/* Checklist list */}
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Loading published checklists...
                </div>
              ) : error ? (
                <div className="p-4 text-center text-destructive">
                  Error: {error instanceof Error ? error.message : "Failed to load checklists"}
                </div>
              ) : filteredChecklists.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {searchTerm ? `No checklists found matching "${searchTerm}"` : "No published checklists available"}
                </div>
              ) : (
                filteredChecklists.map((checklist: any) => (
                  <div
                    key={checklist.id}
                    className="flex items-start space-x-3 p-4 border-b last:border-b-0 hover:bg-accent cursor-pointer"
                    onClick={() => handleChecklistSelect(checklist.id)}
                  >
                    <Checkbox
                      checked={targetChecklistId === checklist.id}
                      onCheckedChange={() => handleChecklistSelect(checklist.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 space-y-1">
                      <div className="font-medium">{checklist.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {checklist.code} • v{checklist.version?.version || '1'}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {targetImportType === 'revision' && !targetChecklistId && filteredChecklists.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Select which published checklist you want to create a revision of
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}